import { createContext, useContext, useState, useEffect } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem("taxinpho_user");
    return u ? JSON.parse(u) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("taxinpho_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        localStorage.setItem("taxinpho_user", JSON.stringify(r.data));
      })
      .catch(() => {
        localStorage.removeItem("taxinpho_token");
        localStorage.removeItem("taxinpho_user");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("taxinpho_token", data.token);
    localStorage.setItem("taxinpho_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("taxinpho_token", data.token);
    localStorage.setItem("taxinpho_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("taxinpho_token");
    localStorage.removeItem("taxinpho_user");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

// Default permissions per role — kept in sync with backend.
const DEFAULT_PERMS = {
  admin: {
    dashboard_view: true, clients_view: true, invoices_view: true, returns_view: true,
    tasks_view: true, expenses_view: true, banks_view: true, cash_view: true,
    clients_write: true, invoices_write: true, returns_write: true, tasks_write: true,
    expenses_write: true, team_manage: true, delete: true, ai: true,
  },
  manager: {
    dashboard_view: true, clients_view: true, invoices_view: true, returns_view: true,
    tasks_view: true, expenses_view: true, banks_view: true, cash_view: true,
    clients_write: true, invoices_write: true, returns_write: true, tasks_write: true,
    expenses_write: true, team_manage: false, delete: true, ai: true,
  },
  staff: {
    dashboard_view: true, clients_view: true, invoices_view: true, returns_view: true,
    tasks_view: true, expenses_view: true, banks_view: true, cash_view: true,
    clients_write: true, invoices_write: true, returns_write: true, tasks_write: true,
    expenses_write: true, team_manage: false, delete: false, ai: true,
  },
  viewer: {
    dashboard_view: true, clients_view: true, invoices_view: true, returns_view: true,
    tasks_view: true, expenses_view: true, banks_view: true, cash_view: true,
    clients_write: false, invoices_write: false, returns_write: false, tasks_write: false,
    expenses_write: false, team_manage: false, delete: false, ai: true,
  },
};

export const defaultPerms = (role) => ({ ...(DEFAULT_PERMS[role] || DEFAULT_PERMS.viewer) });

export const can = (user, perm) => {
  if (!user) return false;
  if (user.role === "admin") return true; // admin always has all rights
  const perms = user.permissions || DEFAULT_PERMS[user.role] || {};
  return !!perms[perm];
};
